/**
 * Lightweight design system for Off Axis Deals.
 * Inline-style friendly (plain TS objects). No Tailwind.
 *
 * Tokens align with the approved marketing references:
 * homepage, /how-it-works, and /pricing.
 *
 * Marketing pages may use larger type and generous section spacing.
 * Authenticated product pages should prefer compact helpers below.
 */

import type { CSSProperties } from 'react';

export const theme = {
  colors: {
    // Primary palette (approved marketing + product)
    primary: '#0ea5e9',
    primaryHover: '#0284c7',
    primaryMuted: '#e0f2fe',
    blue: '#0284c7',
    blueBright: '#0ea5e9',
    navy: '#0f172a',
    navyMid: '#1e293b',
    // Danger / Sign out
    danger: '#b91c1c',
    dangerHover: '#991b1b',
    dangerMuted: '#fef2f2',
    // Success / Post deal
    success: '#059669',
    successHover: '#047857',
    successMuted: '#ecfdf5',
    // Warning / status
    warning: '#b45309',
    warningMuted: '#fffbeb',
    // Neutrals
    ink: '#0f172a',
    inkMuted: '#475569',
    inkSubtle: '#64748b',
    surface: '#ffffff',
    surfaceMuted: '#f8fafc',
    surfaceSubtle: '#f1f5f9',
    border: '#e2e8f0',
    borderMuted: '#cbd5e1',
    // Glass
    glassBg: 'rgba(255, 255, 255, 0.85)',
    glassBorder: 'rgba(226, 232, 240, 0.9)',
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontFamilyMono: 'ui-monospace, "Cascadia Code", "Source Code Pro", monospace',
    // Sizes
    xs: 12,
    sm: 14,
    base: 16,
    lg: 18,
    xl: 20,
    '2xl': 24,
    '3xl': 30,
    '4xl': 36,
    '5xl': 42,
    // Weights
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  radii: {
    sm: 6,
    md: 8,
    lg: 10,
    xl: 12,
    '2xl': 16,
    card: 14,
  },
  shadows: {
    sm: '0 1px 2px rgba(15, 23, 42, 0.05)',
    md: '0 4px 6px -1px rgba(15, 23, 42, 0.07), 0 2px 4px -2px rgba(15, 23, 42, 0.05)',
    lg: '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -4px rgba(15, 23, 42, 0.05)',
    glass: '0 4px 12px rgba(15, 23, 42, 0.06)',
    // Premium panel/card shadows (approved marketing cards)
    panelPremium: '0 6px 20px rgba(15, 23, 42, 0.1), 0 2px 8px rgba(15, 23, 42, 0.05)',
    panelPremiumHover: '0 10px 28px rgba(15, 23, 42, 0.14), 0 4px 10px rgba(15, 23, 42, 0.07)',
    cardMarketing: '0 6px 22px rgba(15, 23, 42, 0.06)',
    cardHover: '0 12px 28px rgba(15, 23, 42, 0.12), 0 4px 12px rgba(15, 23, 42, 0.06)',
    ctaPrimary: '0 4px 14px rgba(14, 165, 233, 0.32)',
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    '2xl': 24,
    '3xl': 32,
    '4xl': 40,
  },
  layout: {
    /** Approved marketing content width (home, how-it-works, pricing) */
    marketingMaxWidth: 1120,
    /** Compact product page content width */
    productMaxWidth: 960,
    /** Wide marketplace / map shells */
    marketplaceMaxWidth: 1920,
    pagePaddingX: 20,
    productPaddingX: 16,
    marketingSectionPaddingY: 'clamp(56px, 7vw, 88px)',
    productSectionPaddingY: 24,
  },
} as const;

// Helper style snippets (return plain objects for spread into style prop)
export const styles = {
  card: (overrides?: CSSProperties): CSSProperties => ({
    background: theme.colors.surface,
    borderRadius: theme.radii.xl,
    boxShadow: theme.shadows.md,
    border: `1px solid ${theme.colors.border}`,
    overflow: 'hidden',
    ...overrides,
  }),

  panel: (overrides?: CSSProperties): CSSProperties => ({
    background: theme.colors.surface,
    borderRadius: theme.radii.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.border}`,
    padding: theme.spacing.lg,
    ...overrides,
  }),

  buttonPrimary: (overrides?: CSSProperties): CSSProperties => ({
    background: theme.colors.primary,
    color: theme.colors.surface,
    border: 'none',
    borderRadius: theme.radii.lg,
    padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
    fontSize: theme.typography.sm,
    fontWeight: theme.typography.semibold,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    transition: 'background 0.15s ease',
    ...overrides,
  }),

  buttonGhost: (overrides?: CSSProperties): CSSProperties => ({
    background: 'transparent',
    color: theme.colors.inkMuted,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.lg,
    padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
    fontSize: theme.typography.sm,
    fontWeight: theme.typography.semibold,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
    ...overrides,
  }),

  buttonDanger: (overrides?: CSSProperties): CSSProperties => ({
    background: theme.colors.danger,
    color: theme.colors.surface,
    border: 'none',
    borderRadius: theme.radii.lg,
    padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
    fontSize: theme.typography.sm,
    fontWeight: theme.typography.semibold,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    transition: 'background 0.15s ease',
    ...overrides,
  }),

  input: (overrides?: CSSProperties): CSSProperties => ({
    width: '100%',
    height: 44,
    minHeight: 44,
    border: `1px solid ${theme.colors.borderMuted}`,
    borderRadius: theme.radii.md,
    padding: `0 ${theme.spacing.md}px`,
    fontSize: theme.typography.base,
    boxSizing: 'border-box',
    ...overrides,
  }),

  label: (overrides?: CSSProperties): CSSProperties => ({
    fontSize: theme.typography.sm,
    color: theme.colors.inkMuted,
    fontWeight: theme.typography.medium,
    marginBottom: theme.spacing.sm,
    display: 'block',
    ...overrides,
  }),

  chip: (overrides?: CSSProperties): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    borderRadius: theme.radii.sm,
    fontSize: theme.typography.xs,
    fontWeight: theme.typography.semibold,
    ...overrides,
  }),

  /** Page background: light gradient + vignette feel */
  pageBackground: (overrides?: CSSProperties): CSSProperties => ({
    background: `linear-gradient(180deg, ${theme.colors.surfaceMuted} 0%, ${theme.colors.surface} 12%, ${theme.colors.surface} 88%, ${theme.colors.surfaceSubtle} 100%)`,
    minHeight: '100%',
    ...overrides,
  }),

  /** Premium panel: stronger shadow, subtle border */
  panelPremium: (overrides?: CSSProperties): CSSProperties => ({
    background: theme.colors.surface,
    borderRadius: theme.radii.xl,
    boxShadow: theme.shadows.panelPremium,
    border: `1px solid ${theme.colors.border}`,
    overflow: 'hidden',
    ...overrides,
  }),

  /** Card hover lift effect (use with onMouseEnter/Leave or CSS) */
  cardHoverLift: (overrides?: CSSProperties): CSSProperties => ({
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    ...overrides,
  }),

  /** Map overlay panel - pointer-events: none on wrapper, auto on controls */
  overlayPanel: (overrides?: CSSProperties): CSSProperties => ({
    position: 'absolute',
    top: theme.spacing.md,
    right: theme.spacing.md,
    zIndex: 10,
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    ...overrides,
  }),

  /** Sticky header for scroll container */
  stickyHeader: (overrides?: CSSProperties): CSSProperties => ({
    position: 'sticky',
    top: 0,
    zIndex: 5,
    background: theme.colors.surface,
    flexShrink: 0,
    ...overrides,
  }),

  /** Small chip for filter badges */
  smallChip: (overrides?: CSSProperties): CSSProperties => ({
    ...styles.chip(),
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
    fontSize: theme.typography.xs,
    ...overrides,
  }),

  /** Marketing section inner (home / how-it-works / pricing) */
  marketingSectionInner: (overrides?: CSSProperties): CSSProperties => ({
    maxWidth: theme.layout.marketingMaxWidth,
    margin: '0 auto',
    padding: `${theme.layout.marketingSectionPaddingY} ${theme.layout.pagePaddingX}px`,
    ...overrides,
  }),

  /** Compact authenticated product page shell */
  productPage: (overrides?: CSSProperties): CSSProperties => ({
    maxWidth: theme.layout.productMaxWidth,
    margin: '0 auto',
    padding: `${theme.layout.productSectionPaddingY}px ${theme.layout.productPaddingX}px`,
    width: '100%',
    boxSizing: 'border-box',
    ...overrides,
  }),

  /** Compact product page title block */
  productPageHeader: (overrides?: CSSProperties): CSSProperties => ({
    marginBottom: theme.spacing['2xl'],
    ...overrides,
  }),

  productPageTitle: (overrides?: CSSProperties): CSSProperties => ({
    margin: '0 0 6px',
    fontSize: theme.typography['2xl'],
    fontWeight: theme.typography.extrabold,
    color: theme.colors.ink,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    ...overrides,
  }),

  productPageSubtitle: (overrides?: CSSProperties): CSSProperties => ({
    margin: 0,
    fontSize: theme.typography.sm,
    color: theme.colors.inkMuted,
    lineHeight: 1.5,
    ...overrides,
  }),

  /** Primary CTA aligned with approved marketing buttons */
  buttonPrimaryMarketing: (overrides?: CSSProperties): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 50,
    padding: '14px 26px',
    borderRadius: theme.radii.md,
    background: theme.colors.primary,
    color: theme.colors.surface,
    fontSize: theme.typography.base,
    fontWeight: theme.typography.extrabold,
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
    boxShadow: theme.shadows.ctaPrimary,
    fontFamily: 'inherit',
    ...overrides,
  }),

  /** Light secondary CTA (blue outline on white) */
  buttonSecondaryLight: (overrides?: CSSProperties): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 50,
    padding: '13px 26px',
    borderRadius: theme.radii.md,
    background: theme.colors.surface,
    color: theme.colors.blue,
    fontSize: theme.typography.base,
    fontWeight: theme.typography.bold,
    textDecoration: 'none',
    border: `1.5px solid ${theme.colors.blue}`,
    cursor: 'pointer',
    boxShadow: 'none',
    fontFamily: 'inherit',
    ...overrides,
  }),

  /** Functional product panel / card */
  productPanel: (overrides?: CSSProperties): CSSProperties => ({
    background: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.card,
    boxShadow: theme.shadows.cardMarketing,
    padding: theme.spacing['2xl'],
    ...overrides,
  }),

  /** Shared empty / loading / error block */
  stateBlock: (overrides?: CSSProperties): CSSProperties => ({
    background: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.xl,
    padding: `${theme.spacing['3xl']}px ${theme.spacing['2xl']}px`,
    textAlign: 'center',
    color: theme.colors.inkMuted,
    ...overrides,
  }),

  statusBadge: (
    tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info' = 'neutral',
    overrides?: CSSProperties,
  ): CSSProperties => {
    const tones: Record<string, CSSProperties> = {
      neutral: {
        background: theme.colors.surfaceSubtle,
        color: theme.colors.inkMuted,
        border: `1px solid ${theme.colors.border}`,
      },
      success: {
        background: theme.colors.successMuted,
        color: theme.colors.successHover,
        border: `1px solid ${theme.colors.successMuted}`,
      },
      warning: {
        background: theme.colors.warningMuted,
        color: theme.colors.warning,
        border: `1px solid ${theme.colors.warningMuted}`,
      },
      danger: {
        background: theme.colors.dangerMuted,
        color: theme.colors.danger,
        border: `1px solid ${theme.colors.dangerMuted}`,
      },
      info: {
        background: theme.colors.primaryMuted,
        color: theme.colors.primaryHover,
        border: `1px solid ${theme.colors.primaryMuted}`,
      },
    };

    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 10px',
      borderRadius: 999,
      fontSize: theme.typography.xs,
      fontWeight: theme.typography.semibold,
      lineHeight: 1.2,
      ...tones[tone],
      ...overrides,
    };
  },
} as const;
